import type { PropsWithChildren } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import {
    StyledManagementCardButton,
    StyledManagementCardIconButton,
} from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/styles";
import { INTEGRATION_REQUEST_ADMIN_STORAGE_KEY_PREFIX } from "app/pages/sales-intelligence/modules/manage-integrations/constants/storageKeys";
import {
    MANAGE_INTEGRATIONS_CARD_UPGRADE,
    MANAGE_INTEGRATIONS_REQUEST_ADMIN_BUTTON_SENT,
    MANAGE_INTEGRATIONS_REQUEST_ADMIN_BUTTON_TEXT,
} from "app/pages/sales-intelligence/modules/manage-integrations/constants/translations";
import { useRequestAdminAccess } from "app/pages/sales-intelligence/modules/manage-integrations/hooks/useRequestAdminAccess";
import type { RequestAdminFeature } from "app/pages/sales-intelligence/types/requestAdminFeature";

export interface IIntegrationCardButtonWrapperProps {
    dataAutomationNameSpace: string;
    featureName?: RequestAdminFeature;
    hasAccess?: boolean;
    isEnabled?: boolean;
    upgradeButtonText?: string;
    onUpgradeClick?: VoidFunction;
    onRequestAccess?: VoidFunction;
}

const IntegrationButtonWrapper = ({
    dataAutomationNameSpace,
    featureName,
    hasAccess = true,
    isEnabled = true,
    upgradeButtonText,
    onUpgradeClick,
    onRequestAccess,
    children,
}: PropsWithChildren<IIntegrationCardButtonWrapperProps>): React.ReactNode => {
    const { translate } = useTranslate();
    const { isSent, requestAccess } = useRequestAdminAccess(INTEGRATION_REQUEST_ADMIN_STORAGE_KEY_PREFIX, featureName);

    const handleRequestAccess = () => {
        requestAccess();

        if (onRequestAccess) {
            onRequestAccess();
        }
    };

    if (!hasAccess && featureName) {
        return (
            <StyledManagementCardButton
                buttonHtmlType="button"
                type="primary"
                onClick={isSent ? undefined : handleRequestAccess}
                isDisabled={isSent}
                data-automation={`${dataAutomationNameSpace}.integration-card-request-admin-button`}
            >
                {translate(
                    isSent
                        ? MANAGE_INTEGRATIONS_REQUEST_ADMIN_BUTTON_SENT
                        : MANAGE_INTEGRATIONS_REQUEST_ADMIN_BUTTON_TEXT,
                )}
            </StyledManagementCardButton>
        );
    }

    if (!isEnabled) {
        return (
            <StyledManagementCardIconButton
                buttonHtmlType="button"
                type="trial"
                iconName="sparkles-gray"
                iconSize="xs"
                onClick={onUpgradeClick}
                data-automation={`${dataAutomationNameSpace}.integration-card-upgrade-button`}
            >
                {upgradeButtonText || translate(MANAGE_INTEGRATIONS_CARD_UPGRADE)}
            </StyledManagementCardIconButton>
        );
    }

    return children;
};

export default IntegrationButtonWrapper;
